/**
 * Vesper API Endpoint Constants (Shared)
 *
 * Platform-agnostic — base URL is set via initApiBase().
 */

let _apiBase = 'http://localhost:3001/api/v1';

export function initApiBase(baseUrl: string): void {
  _apiBase = baseUrl;
}

export function getApiBase(): string {
  return _apiBase;
}

export const API = {
  // ── Auth ────────────────────────────────────────────────────────────────────
  REGISTER:          '/auth/register',
  LOGIN:             '/auth/login',
  REFRESH_TOKEN:     '/auth/refresh',

  // ── Own profile ─────────────────────────────────────────────────────────────
  ME:                '/users/me',
  MY_SCORE:          '/users/me/score',
  MY_RELIABILITY:    '/users/me/reliability',
  MY_EVENT_HISTORY:  '/users/me/events',
  MY_PRIVACY:        '/users/me/privacy',
  MY_VERIFY:         (type: 'face' | 'id') => `/users/me/verify/${type}` as const,
  MY_AVATAR:         '/users/me/avatar',

  // ── Location ─────────────────────────────────────────────────────────────────
  LOCATION:          '/users/location',
  SPOOF_LOCATION:    '/users/me/spoof-location',

  // ── Other users ──────────────────────────────────────────────────────────────
  USER_BY_ID:        (id: string) => `/users/${id}` as const,
  USER_SCORE:        (id: string) => `/users/${id}/score` as const,
  MUTUAL_FRIENDS:    (id: string) => `/users/${id}/mutual-friends` as const,

  // ── Events ───────────────────────────────────────────────────────────────────
  EVENTS:            '/events',
  EVENT_BY_ID:       (id: string) => `/events/${id}` as const,
  EVENT_INVITE:      (id: string) => `/events/${id}/invite` as const,
  EVENT_RESPOND:     (id: string) => `/events/${id}/respond` as const,
  EVENT_SWAP:        (id: string) => `/events/${id}/swap` as const,
  EVENT_CANCEL:      (id: string) => `/events/${id}/cancel` as const,
  EVENT_CANCEL_EVENT:(id: string) => `/events/${id}/cancel-event` as const,
  EVENT_COMPLETE:    (id: string) => `/events/${id}/complete` as const,
  EVENT_SUGGESTIONS: (id: string) => `/events/${id}/suggestions` as const,
  EVENT_REPLACEMENT_CANDIDATES: (id: string) => `/events/${id}/replacement-candidates` as const,
  EVENT_INVITE_REPLACEMENT: (id: string) => `/events/${id}/invite-replacement` as const,
  EVENT_ACCEPT_REPLACEMENT: (id: string) => `/events/${id}/accept-replacement` as const,
  EVENT_ATTENDANCE_ME:     (id: string) => `/events/${id}/attendance/me` as const,
  EVENT_MANUAL_CHECK_IN:   (id: string, userId: string) => `/events/${id}/attendees/${userId}/check-in` as const,

  // ── Event Prompts (pre-screening flow) ────────────────────────────────────────
  EVENT_PROMPTS:              '/events/prompts',
  EVENT_PROMPT_SCREEN:        '/events/prompts/screening',
  EVENT_PROMPT_BY_ID:         (id: string) => `/events/prompts/${id}` as const,
  EVENT_PROMPT_APPROVE:       (id: string) => `/events/prompts/${id}/approve-group` as const,
  EVENT_PROMPT_SWAP:          (id: string) => `/events/prompts/${id}/swap-candidate` as const,
  EVENT_PROMPT_GROUP_STATUS:  (id: string) => `/events/prompts/${id}/group-status` as const,
  EVENT_PROMPT_RESPOND:       (id: string) => `/events/prompts/${id}/screen` as const,
  EVENT_PROMPT_CONVERT:       (id: string) => `/events/prompts/${id}/convert` as const,
  EVENT_PROMPT_CANCEL:        (id: string) => `/events/prompts/${id}/cancel` as const,
  EVENT_PROMPT_REROLL:        (id: string) => `/events/prompts/${id}/reroll` as const,
  EVENT_PROMPT_RESCHEDULE:    (id: string) => `/events/prompts/${id}/reschedule` as const,
  EVENT_PROMPT_ZOOM_URL:      (id: string) => `/events/prompts/${id}/zoom-url` as const,

  // ── Notifications ─────────────────────────────────────────────────────────────
  NOTIFICATIONS:                '/notifications',
  NOTIFICATIONS_UNREAD_COUNT:   '/notifications/unread-count',
  NOTIFICATIONS_UNREAD_BY_TYPE: '/notifications/unread-by-type',
  NOTIFICATIONS_READ_ALL:       '/notifications/read-all',
  NOTIFICATION_READ:            (id: string) => `/notifications/${id}/read` as const,
  NOTIFICATIONS_SOURCE_READ:    (sourceId: string) => `/notifications/source/${sourceId}/read` as const,

  // ── Right Now ─────────────────────────────────────────────────────────────────
  RIGHT_NOW:         '/right-now',
  RIGHT_NOW_FEED:    '/right-now',
  RIGHT_NOW_MINE:    '/right-now/mine',
  RIGHT_NOW_UPLOAD:  '/right-now/upload',
  RIGHT_NOW_DELETE:  (id: string) => `/right-now/${id}` as const,

  // ── Messages ────────────────────────────────────────────────────────────────
  CONVERSATIONS:     '/messages/conversations',
  EVENT_MESSAGES:    (id: string) => `/events/${id}/messages` as const,

  // ── Cleo + Ratings + Bench ───────────────────────────────────────────────────
  ARK_REEMBED:       '/users/me/reembed',
  ARK_SESSION:       '/cleo/session',
  ARK_CHAT:          '/cleo/chat',
  ARK_CHAT_STREAM:   '/cleo/chat-stream',
  ARK_SELECT_EXTERNAL: '/cleo/select-external',
  ARK_SKIP_EXTERNAL:   '/cleo/skip-external',
  ARK_SELECT_VENUE:    '/cleo/select-venue',
  ARK_REJECT_VENUES:   '/cleo/reject-venues',
  ARK_ABANDON_SESSION: '/cleo/abandon-session',
  ARK_ABANDON_ACTIVE_SESSION: '/cleo/abandon-active-session',
  ARK_COMPLETE_SESSION: '/cleo/complete-session',
  ARK_RATE_SESSION:  (eventId: string) => `/cleo/events/${eventId}/rate-session` as const,
  MY_RATINGS:        '/cleo/ratings',
  FRIEND_REQUEST:    '/cleo/friends/request',
  FRIEND_INCOMING:   '/cleo/friends/incoming',
  FRIEND_SENT:       '/cleo/friends/sent',
  FRIEND_DECLINE:    (id: string) => `/cleo/friends/request/${id}` as const,
  FRIEND_REMOVE:     (id: string) => `/cleo/bench/${id}` as const,
  MY_BENCH:          '/cleo/bench',

  // ── Geo ─────────────────────────────────────────────────────────────────────
  GEO_REVERSE:       '/geo/reverse',
  GEO_NEARBY_ALL:    '/geo/nearby-all',
  GEO_NEARBY_USERS:  '/geo/nearby-users',
  GEO_PLACE_SEARCH:  '/geo/place-search',
  GEO_PLACE_TALLY:   '/geo/place-tally',

  // ── Venues ───────────────────────────────────────────────────────────────────
  VENUE_REGISTER:      '/venues/register',
  VENUE_MY_ACCESS:     '/venues/my-access',
  VENUE_BY_ID:         (id: string) => `/venues/${id}` as const,
  VENUE_DOCUMENTS:     (id: string) => `/venues/${id}/documents` as const,
  VENUE_STAFF:         (id: string) => `/venues/${id}/staff` as const,
  VENUE_STAFF_REMOVE:  (venueId: string, userId: string) => `/venues/${venueId}/staff/${userId}` as const,
  VENUE_NOTICES:       (venueId: string) => `/venues/${venueId}/notices` as const,
  VENUE_NOTICE_RESPOND:(venueId: string, noticeId: string) => `/venues/${venueId}/notices/${noticeId}/respond` as const,

  // ── Public Events ─────────────────────────────────────────────────────────────
  PUBLIC_EVENTS:              '/public-events',
  PUBLIC_EVENT_COVER_UPLOAD:  '/public-events/cover-image-url',
  PUBLIC_EVENT_BY_ID:         (id: string) => `/public-events/${id}` as const,
  PUBLIC_EVENT_ATTENDEES:     (id: string) => `/public-events/${id}/attendees` as const,
  PUBLIC_EVENTS_PLACES_SEARCH: '/public-events/places-search',
  PUBLIC_EVENTS_VENUE_UPCOMING: (venueId: string) => `/public-events/venue/${venueId}/upcoming` as const,
  PUBLIC_EVENT_MATCH:           (id: string) => `/public-events/${id}/match` as const,

  // ── Cleo Explore ───────────────────────────────────────────────────────────
  ARK_EXPLORE:              '/cleo/explore',
  ARK_EXPLORE_STREAM:       '/cleo/explore-stream',
  ARK_EXPLORE_INITIAL:      '/cleo/explore/initial',

  // ── Saved Events ──────────────────────────────────────────────────────────
  PUBLIC_EVENT_SAVE:        (id: string) => `/public-events/${id}/save` as const,
  PUBLIC_EVENTS_SAVED:      '/public-events/saved',
  PUBLIC_EVENTS_SAVED_IDS:  '/public-events/saved/ids',

  // ── Event Seeder ──────────────────────────────────────────────────────────
  SEEDER_SEARCH:  '/seeder/search',
  SEEDER_CREATE:  '/seeder/create',

  // ── Direct Messages ───────────────────────────────────────────────────
  DM_START:          '/messages/dm',
  DM_CREATE_GROUP:   '/messages/dm/group',
  DM_CONVERSATIONS:  '/messages/dm/conversations',
  DM_MESSAGES:       (id: string) => `/messages/dm/${id}` as const,
  DM_SEND:           (id: string) => `/messages/dm/${id}` as const,
  DM_MEMBERS:        (id: string) => `/messages/dm/${id}/members` as const,
  DM_LEAVE:          (id: string) => `/messages/dm/${id}/leave` as const,

  // ── For You (Discover surface) ────────────────────────────────────────
  FOR_YOU_WEEKLY:    '/for-you/weekly',
  FOR_YOU_EVENTS:    '/for-you/events',

  // ── Connections (Vesper Phase 6) ──────────────────────────────────────
  COLD_CONNECT_QUOTA: '/users/me/cold-connect-quota',
  MY_SCORE_HISTORY:   '/users/me/score-history',

  // ── Updates feed (Vesper Phase 8) ─────────────────────────────────────
  UPDATES_FEED:       '/updates/feed',
  UPDATES_SAVED:      '/updates/saved',
  UPDATES_BY_AUTHOR:  (id: string) => `/updates/users/${id}` as const,
  UPDATES_BY_ID:      (id: string) => `/updates/${id}` as const,
  UPDATES_CREATE:     '/updates',
  UPDATES_UPLOAD:     '/updates/upload',
  UPDATES_SAVE:       (id: string) => `/updates/${id}/save` as const,
  UPDATES_VOUCH:      (id: string) => `/updates/${id}/vouch` as const,
  UPDATES_VIEW:       (id: string) => `/updates/${id}/view` as const,
  UPDATE_DELETE:      (id: string) => `/updates/${id}` as const,
  UPDATE_HIDE:        (id: string) => `/updates/${id}/hide` as const,
};
